// src/oura/components/docs/MarkDoneDialog.tsx
//
// Mark as done, asked in a dialog (#4541). It used to open inline in the row,
// where the date field, Today, Mark done, Cancel and the hint crowded the row's
// own chips and ran off its edge. Same shell as DeleteDocDialog, with the
// positive (blue) icon.
//
// It asks the day the work was done, empty by default. Empty, the server uses
// the date of the Word file the doc was converted from, else the doc's own date
// (its name's date, else the day it was made): backend 37f1af8c4,
// oura_doc_done.work_day. A day after today is refused here and by the server.
//
// Drawn in a portal on <body>: the row it opens from is a size container
// (`container-type: inline-size`), which holds `position: fixed` inside it.

import { useEffect, useId, useState } from "react";
import { createPortal } from "react-dom";
import { CheckCircle2Icon, CheckIcon, Loader2Icon } from "lucide-react";
import { markDocumentDone, type StaffDocument } from "@service/aeo/clientDocs";
import { cn } from "../../lib/cn";
import { PillButton, TYPE } from "@oura/ui";
import { Button } from "../../primitives/Button";
import { myToday } from "./boardModel";

/** Refused by the server when the work day is after today (ourteam-backend#6414), and checked here first. */
const DONE_ON_FUTURE = "The day the work was done cannot be after today.";
/** The date field: the same look as the Date in the name field on Create. */
const FIELD = cn(TYPE.tb, "block h-11 w-auto rounded-xl border-0 bg-gray-50 px-3 text-gray-900 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-600 sm:h-9");

export function MarkDoneDialog({ doc, title, open, onClose }: { doc: StaffDocument; title: string; open: boolean; onClose: () => void }) {
    const [day, setDay] = useState("");
    const [saving, setSaving] = useState(false);
    const [failed, setFailed] = useState<string | null>(null);
    const fieldId = useId();
    const errorId = useId();
    const titleId = useId();
    // Each opening starts empty: the server's own date is the default.
    useEffect(() => {
        setDay("");
        setFailed(null);
    }, [open, doc.id]);

    if (!open) return null;

    const today = myToday();
    const confirm = async () => {
        if (saving) return;
        if (day > today) return setFailed(DONE_ON_FUTURE);
        setSaving(true);
        setFailed(null);
        const r = await markDocumentDone(doc.id, true, day || null);
        setSaving(false);
        // On success the board re-reads (thenEmit) and the row moves to Done.
        if (r.ok) onClose();
        else setFailed(r.code === "done_on_future" ? DONE_ON_FUTURE : "That did not go through. Try again.");
    };
    const cancel = () => !saving && onClose();

    return createPortal(
        <div
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            role="dialog"
            aria-modal="true"
            aria-labelledby={titleId}
            data-doc-done-dialog={doc.id}
            // A portal still bubbles through React to the row's parents: keep clicks here.
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => {
                e.stopPropagation();
                if (e.key === "Escape") cancel();
            }}
        >
            {/* No dismiss on the backdrop while the request is in flight. */}
            <div className="absolute inset-0 bg-gray-900/50" onClick={cancel} />

            <form
                noValidate
                className="relative w-full max-w-md rounded-lg bg-white shadow-xl"
                onSubmit={(e) => {
                    e.preventDefault();
                    void confirm();
                }}
            >
                <div className="flex items-start gap-3 px-6 pt-5">
                    <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-blue-50">
                        <CheckCircle2Icon className="h-5 w-5 text-blue-600" />
                    </div>
                    <div className="min-w-0 pt-1.5">
                        <h2 id={titleId} className="text-base font-semibold text-gray-900">
                            Mark this doc as done?
                        </h2>
                        <p className="m-0 mt-1 break-words text-sm text-gray-500">{title}</p>
                    </div>
                </div>

                <div className="space-y-2 px-6 pt-4">
                    <label htmlFor={fieldId} className="block text-sm font-medium text-gray-900">
                        Work done on
                    </label>
                    <div className="flex flex-wrap items-center gap-2">
                        <input
                            id={fieldId}
                            type="date"
                            autoFocus
                            max={today}
                            value={day}
                            disabled={saving}
                            aria-invalid={failed ? true : undefined}
                            aria-describedby={failed ? errorId : undefined}
                            onChange={(e) => {
                                setDay(e.target.value);
                                setFailed(null);
                            }}
                            className={FIELD}
                        />
                        {day !== today && (
                            <PillButton variant="text" size="sm" disabled={saving} onClick={() => setDay(today)}>
                                Today
                            </PillButton>
                        )}
                    </div>
                    <p className="m-0 text-xs leading-relaxed text-gray-500">
                        {day
                            ? "The doc shows as done on this day."
                            : <>Left empty, the date of the Word file it was converted from is used, else the date in the doc&rsquo;s name.</>}
                    </p>
                    {failed && (
                        <p id={errorId} role="alert" className="m-0 text-sm text-rose-600">
                            {failed}
                        </p>
                    )}
                </div>

                <div className="flex gap-2 px-6 pb-5 pt-5">
                    <Button type="button" variant="outline" className="flex-1" disabled={saving} onClick={cancel}>
                        Cancel
                    </Button>
                    <Button type="submit" className="flex-1" disabled={saving} data-doc-done-confirm={doc.id} aria-label={`Mark done: ${title}`}>
                        {saving ? (
                            <>
                                <Loader2Icon className="h-4 w-4 animate-spin" />
                                Saving…
                            </>
                        ) : (
                            <>
                                <CheckIcon className="h-4 w-4" />
                                Mark as done
                            </>
                        )}
                    </Button>
                </div>
            </form>
        </div>,
        document.body,
    );
}
